import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import BouncedEmailDetailsContext from '@totem/components/administration/bouncedEmails/details/BouncedEmailDetailsContext';
import {
  BouncedEmailActionResponse,
  BouncedEmailDetails,
  StepResult,
} from '@totem/components/administration/bouncedEmails/details/types';
import { BouncedEmailContact } from '@totem/components/administration/bouncedEmails/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BOUNCED_EMAILS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  email: string;
  children?: ReactNode;
};

const BouncedEmailDetailsContainer = ({ email, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [working, setWorking] = useState<boolean>(false);
  const [details, setDetails] = useState<BouncedEmailDetails | null>(null);
  const [lastSteps, setLastSteps] = useState<StepResult[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(true);

  const baseUrl = `${BOUNCED_EMAILS_ENDPOINT}/${encodeURIComponent(email)}`;

  useEffect(() => {
    if (!refreshData || !email) {
      return;
    }

    setRefreshData(false);

    setLoading(true);

    fetch(baseUrl, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(res.statusText);
        }
        return res.json();
      })
      .then((result: BouncedEmailDetails) => {
        setDetails(result ?? null);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load bounced email.' });
      })
      .finally(() => setLoading(false));
  }, [refreshData, email, baseUrl, getApiToken]);

  const runAction = useCallback(
    (
      path: string,
      method: string,
      body: object | null,
      successMessage: string,
    ) => {
      setWorking(true);

      fetch(`${baseUrl}${path}`, {
        method,
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
          'Content-Type': 'application/json',
        }),
        body: body ? JSON.stringify(body) : undefined,
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(res.statusText);
          }
          return res.json();
        })
        .then((result: BouncedEmailActionResponse) => {
          setDetails(result?.details ?? null);

          setLastSteps(result?.results?.steps ?? []);

          if (result?.results && !result.results.success) {
            notification.warning({
              message: 'Some steps failed — see results below.',
            });
          } else {
            notification.success({ message: successMessage });
          }
        })
        .catch(() => {
          notification.error({ message: 'Action failed.' });
        })
        .finally(() => setWorking(false));
    },
    [baseUrl, getApiToken],
  );

  return (
    <BouncedEmailDetailsContext.Provider
      value={{
        loading,
        working,
        details,
        lastSteps,
        onRefresh: () => setRefreshData(true),
        setStatus: (status: string) =>
          runAction('/status', 'PATCH', { status }, `Status set to ${status}.`),
        removeContact: (contact: BouncedEmailContact) =>
          runAction(
            '/contacts/remove',
            'POST',
            { type: contact.type, id: contact.id },
            'Contact removed.',
          ),
        disableUser: () =>
          runAction('/user/disable', 'POST', null, 'User account disabled.'),
        disableRemoteAccess: () =>
          runAction(
            '/remoteAccess/disable',
            'POST',
            null,
            'Removed from all remote access roles.',
          ),
      }}
    >
      {children}
    </BouncedEmailDetailsContext.Provider>
  );
};

export default BouncedEmailDetailsContainer;
